import React, { useEffect, useState, useRef, useCallback } from "react";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import {
  Box,
  LinearProgress,
  Typography,
  Paper,
  Fade,
  Chip,
  Alert,
  Container,
  Stack,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Card,
  CardContent,
  CircularProgress,
} from "@mui/material";

import {
  KITCHEN_API_URL,
  UTENSIL_CHECK_API_URL,
  SOCKET_TIMEOUT,
  SECURITY_CHECKS,
} from "../constants";
import { generateCode } from "../Static/code-generator";
import { useKitchenSocket } from "../Hooks/useKitchenSocket";
import { useUtensilCheckSocket } from "../Hooks/useUtensilCheckSocket";
import { gql, useMutation, useQuery } from "@apollo/client";
import { ConnectionStatus } from "../Components/CookingProcess/ConnectionStatus";
import { SecurityChecks } from "../Components/CookingProcess/SecurityChecks";
import { CookingInProgress } from "../Components/CookingProcess/CookingInProgress";
import { CookingSuccess } from "../Components/CookingProcess/CookingSuccess";
import { CookingFailure } from "../Components/CookingProcess/CookingFailure";
import KitchenFlow from "../Components/CookingProcess/KitchenFlow";
import ErrorPage from "./ErrorPage";

const COOKING_STATUS_MUTATION = gql`
  mutation ReportCookingStatus($status: String!) {
    reportCookingStatus(status: $status) @client
  }
`;

const UPDATE_COOKING_HISTORY_STATUS = gql`
  mutation UpdateCookingHistoryStatus(
    $id: ID!
    $cooking_status: CookingStatus!
  ) {
    updateCookingHistoryStatus(id: $id, cooking_status: $cooking_status) {
      id
      cooking_status
    }
  }
`;

// Add GraphQL query for sensor settings
const GET_USER_SENSOR_SETTINGS = gql`
  query GetUserSensorSettings {
    getUserSensorSettings {
      id
      debugMode
      sensors {
        id
        name
        position
        isEnabled
        isSkipped
      }
      enableSignalCheck
      enableUtensilCheck
      enableDispenserCheck
    }
  }
`;

const CookingProcess = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { recipe_id } = useParams();
  const { state } = useLocation();

  // Get user sensor settings for enhanced signal checking
  const { data: sensorSettingsData } = useQuery(GET_USER_SENSOR_SETTINGS, {
    errorPolicy: "ignore",
  });

  // Get settings for conditional checking - only apply when data is loaded
  const settingsLoaded = !!sensorSettingsData?.getUserSensorSettings;
  const enableSignalCheck = settingsLoaded
    ? sensorSettingsData.getUserSensorSettings.enableSignalCheck === true
    : true; // Default to true when not loaded
  const enableUtensilCheck = settingsLoaded
    ? sensorSettingsData.getUserSensorSettings.enableUtensilCheck === true
    : true; // Default to true when not loaded
  const enableDispenserCheck = settingsLoaded
    ? sensorSettingsData.getUserSensorSettings.enableDispenserCheck === true
    : true; // Default to true when not loaded

  // Enhanced data parsing with error handling
  const [parsedData, setParsedData] = useState({
    tasks: [],
    recipe: null,
    code: state.code,
    cookingHistoryId: null,
    parseError: null,
  });

  // Parse incoming data
  useEffect(() => {
    try {
      let tasks = [];
      let recipe = null;
      let code = state.code;
      let cookingHistoryId = null;

      if (state) {
        // Handle new API format where recipe_tasks is a JSON string
        if (state.recipe_tasks) {
          try {
            tasks =
              typeof state.recipe_tasks === "string"
                ? JSON.parse(state.recipe_tasks)
                : state.recipe_tasks;
          } catch (parseError) {
            console.error("Error parsing recipe_tasks:", parseError);
            tasks = [];
          }
        } else if (state.tasks) {
          // Fallback to old format
          tasks = state.tasks;
        }

        // Handle recipe data
        if (state.recipe_name) {
          // New API format
          recipe = {
            id: state.id,
            recipe_name: state.recipe_name,
            recipe_type: state.recipe_type,
            recipe_category: state.recipe_category,
            recipe_cooking_duration: state.recipe_cooking_duration,
            cook_time: state.cook_time,
            prep_time: state.prep_time,
            recipe_image: state.recipe_image,
            merged_recipe_names: state.merged_recipe_names || [],
            serving: state.serving,
          };
        } else {
          // Old format
          recipe = state.recipe;
        }

        code = state.code;
        cookingHistoryId = state.cookingHistoryId;
      }

      // Validate and enhance tasks
      const validatedTasks = Array.isArray(tasks)
        ? tasks.map((task, index) => ({
            ...task,
            id: task.id || `task_${index}`,
            duration: Math.max(1, parseInt(task.duration) || 1), // Ensure minimum duration of 1 second
            name: task.name || `Step ${index + 1}`,
            category: task.category || "Action",
            valid: task.valid !== false, // Default to true if not specified
            complete: task.complete || 0,
            errorMessage: task.errorMessage || "",
          }))
        : [];

      setParsedData({
        tasks: validatedTasks,
        recipe,
        code: state.code,
        cookingHistoryId,
        parseError: null,
      });
    } catch (error) {
      console.error("Error processing cooking data:", error);
      setParsedData({
        tasks: [],
        recipe: null,
        code: state.code,
        cookingHistoryId: null,
        parseError: error.message,
      });
    }
  }, [state]);

  const { tasks, recipe, cookingHistoryId, parseError, code } = parsedData;
  const isMergedRecipes = recipe?.merged_recipe_names?.length > 1;

  // Determine which pots are needed
  const hasNP1 = tasks.some((t) => t.cookIn === "np1");
  const hasNP2 = tasks.some((t) => t.cookIn === "np2");

  // Socket management for kitchen control
  const {
    socket,
    isConnected,
    connectionError,
    emitWithTimeout,
    checkSignals,
    resetSocket,
  } = useKitchenSocket(KITCHEN_API_URL);

  // Socket management for utensil verification - conditionally enabled
  const {
    isConnected: isUtensilSocketConnected,
    connectionError: utensilConnectionError,
    checkUtensil,
  } = useUtensilCheckSocket(
    settingsLoaded && enableUtensilCheck ? UTENSIL_CHECK_API_URL : null
  );

  // State management
  const [cookingStatus, setCookingStatus] = useState("pre-connection");
  const [elapsedTime, setElapsedTime] = useState(0);
  const [securityChecks, setSecurityChecks] = useState([]);
  const [completedChecks, setCompletedChecks] = useState(new Set());
  const [resetStatus, setResetStatus] = useState({
    loading: false,
    error: null,
    tried: false,
  });
  const [transition, setTransition] = useState(true);

  // New state for the clear flow
  const [flowStage, setFlowStage] = useState("waiting-connections"); // waiting-connections, checking-signals, checking-utensils, checking-dispensers, security-checks, cooking

  // State for dispenser checking
  const [dispenserCheckStatus, setDispenserCheckStatus] = useState({
    isChecking: false,
    grainStatus: null, // null, "checking", "success", "failed"
    masalaStatus: null, // null, "checking", "success", "failed"
    error: null,
    completed: false,
  });

  const timerRef = useRef(null);
  const [reportCookingStatus] = useMutation(COOKING_STATUS_MUTATION);
  const [updateCookingHistoryStatus] = useMutation(
    UPDATE_COOKING_HISTORY_STATUS
  );

  // Mock cooking function for testing
  // const isCooked = async () => {
  //   await new Promise((resolve) => setTimeout(resolve, 1000000)); // Fake timeout for testing
  //   return true;
  // };

  // Helper function to check if recipe has dispenser tasks
  const hasDispenserTasks = useCallback(() => {
    const hasGrainTask = tasks.some(
      (task) => task.type === "choose_grains" || task.type === "choose_soaks"
    );
    const hasSpiceTask = tasks.some((task) => task.type === "choose_spices");
    return hasGrainTask || hasSpiceTask;
  }, [tasks]);

  // Helper function to get first grain task for testing (includes both choose_grains and choose_soaks)
  const getFirstGrainTask = useCallback(() => {
    return tasks.find(
      (task) => task.type === "choose_grains" || task.type === "choose_soaks"
    );
  }, [tasks]);

  // Helper function to get first spice task for testing
  const getFirstSpiceTask = useCallback(() => {
    return tasks.find((task) => task.type === "choose_spices");
  }, [tasks]);

  // Function to check dispensers with new flow (individual checks + smart retry)
  const checkDispensers = useCallback(
    async (retryMode = false) => {
      try {
        const currentStatus = dispenserCheckStatus || {};

        // In retry mode, preserve successful statuses and only reset failed ones
        if (retryMode) {
          setDispenserCheckStatus((prev) => ({
            ...prev,
            isChecking: true,
            error: null,
            completed: false,
            // Reset only failed dispensers to null for re-checking
            grainStatus: prev.grainStatus === "success" ? "success" : null,
            masalaStatus: prev.masalaStatus === "success" ? "success" : null,
          }));
        } else {
          // First time check - reset everything
          setDispenserCheckStatus({
            isChecking: true,
            grainStatus: null,
            masalaStatus: null,
            error: null,
            completed: false,
          });
        }

        const firstGrainTask = getFirstGrainTask();
        const firstSpiceTask = getFirstSpiceTask();

        // Create updated tasks by removing first grain and spice tasks
        const updatedTasks = tasks.filter((task) => {
          if (firstGrainTask && task.id === firstGrainTask.id) return false;
          if (firstSpiceTask && task.id === firstSpiceTask.id) return false;
          return true;
        });

        console.log("🔧 Original tasks count:", tasks.length);
        console.log("🔧 Updated tasks count:", updatedTasks.length);
        console.log("🔧 Removed grain/soak task:", firstGrainTask?.name);
        console.log("🔧 Removed spice task:", firstSpiceTask?.name);

        let masalaError = null;
        let grainError = null;

        // Check Masala Bowl if we have a spice task and it's not already successful
        if (firstSpiceTask && currentStatus.masalaStatus !== "success") {
          console.log(
            "🌶️ Checking masala dispenser with task:",
            firstSpiceTask
          );
          setDispenserCheckStatus((prev) => ({
            ...prev,
            masalaStatus: "checking",
          }));

          try {
            // Generate masala check code with full quantity
            const masalaCheckCode = generateMasalaCheckCode(firstSpiceTask);
            console.log(
              `🌶️ Dispensing full quantity for spice check: Box ${firstSpiceTask.position}, Quantity ${firstSpiceTask.quantity}g`
            );
            console.log(`🌶️ Sending masala check code:`, masalaCheckCode);

            const masalaResult = await emitWithTimeout(
              "cook-now",
              masalaCheckCode,
              SOCKET_TIMEOUT
            );

            console.log("🌶️ Masala dispenser result:", masalaResult);

            if (masalaResult === true) {
              setDispenserCheckStatus((prev) => ({
                ...prev,
                masalaStatus: "success",
              }));
              console.log("✅ Masala dispenser check passed");
            } else {
              setDispenserCheckStatus((prev) => ({
                ...prev,
                masalaStatus: "failed",
              }));
              console.log("❌ Masala dispenser check failed");
              masalaError = "Masala dispenser check failed";
            }
          } catch (error) {
            console.error("❌ Error checking masala dispenser:", error);
            setDispenserCheckStatus((prev) => ({
              ...prev,
              masalaStatus: "failed",
            }));
            masalaError = error.message;
          }
        } else if (firstSpiceTask && currentStatus.masalaStatus === "success") {
          console.log("✅ Masala dispenser already successful, skipping...");
        }

        // Check Grain Bowl if we have a grain task and it's not already successful
        if (firstGrainTask && currentStatus.grainStatus !== "success") {
          console.log("🌾 Checking grain dispenser with task:", firstGrainTask);
          setDispenserCheckStatus((prev) => ({
            ...prev,
            grainStatus: "checking",
          }));

          try {
            // Generate grain check code with full quantity
            const grainCheckCode = generateGrainCheckCode(firstGrainTask);
            console.log(
              `🌾 Dispensing full quantity for grain/soak check: Box ${firstGrainTask.position}, Quantity ${firstGrainTask.quantity}g (${firstGrainTask.type})`
            );
            console.log(`🌾 Sending grain check code:`, grainCheckCode);

            const grainResult = await emitWithTimeout(
              "cook-now",
              grainCheckCode,
              SOCKET_TIMEOUT
            );

            console.log("🌾 Grain dispenser result:", grainResult);

            if (grainResult === true) {
              setDispenserCheckStatus((prev) => ({
                ...prev,
                grainStatus: "success",
              }));
              console.log("✅ Grain dispenser check passed");
            } else {
              setDispenserCheckStatus((prev) => ({
                ...prev,
                grainStatus: "failed",
              }));
              console.log("❌ Grain dispenser check failed");
              grainError = "Grain dispenser check failed";
            }
          } catch (error) {
            console.error("❌ Error checking grain dispenser:", error);
            setDispenserCheckStatus((prev) => ({
              ...prev,
              grainStatus: "failed",
            }));
            grainError = error.message;
          }
        } else if (firstGrainTask && currentStatus.grainStatus === "success") {
          console.log("✅ Grain dispenser already successful, skipping...");
        }

        // Determine final statuses based on actual test results
        let finalGrainStatus = currentStatus.grainStatus;
        let finalMasalaStatus = currentStatus.masalaStatus;

        // Update with current test results from this execution
        if (firstGrainTask && currentStatus.grainStatus !== "success") {
          finalGrainStatus = grainError ? "failed" : "success";
        }
        if (firstSpiceTask && currentStatus.masalaStatus !== "success") {
          finalMasalaStatus = masalaError ? "failed" : "success";
        }

        const allPassed =
          (!firstGrainTask || finalGrainStatus === "success") &&
          (!firstSpiceTask || finalMasalaStatus === "success");

        console.log("🔧 Final grain status:", finalGrainStatus);
        console.log("🔧 Final masala status:", finalMasalaStatus);
        console.log("🔧 All passed:", allPassed);

        // Store updated tasks and codes for later use
        setDispenserCheckStatus((prev) => ({
          ...prev,
          isChecking: false,
          completed: true,
          updatedTasks,
          originalTasks: tasks,
          error: masalaError || grainError || null,
          // Update with final statuses
          grainStatus: finalGrainStatus,
          masalaStatus: finalMasalaStatus,
        }));

        if (allPassed) {
          console.log(
            "✅ All dispenser checks passed, proceeding to security checks..."
          );
          setTimeout(() => {
            setFlowStage("security-checks");
          }, 2000);
        } else {
          console.log("⚠️ Some dispensers failed, waiting for user action...");
        }
      } catch (error) {
        console.error("❌ Error during dispenser check:", error);
        setDispenserCheckStatus((prev) => ({
          ...prev,
          isChecking: false,
          completed: true,
          error: error.message,
        }));
      }
    },
    [
      getFirstGrainTask,
      getFirstSpiceTask,
      emitWithTimeout,
      tasks,
      dispenserCheckStatus,
    ]
  );

  // Generate masala check code using existing task code with full quantity
  const generateMasalaCheckCode = useCallback((spiceTask) => {
    // Use full quantity since this task is removed from main cooking
    return generateCode([spiceTask]);
  }, []);

  // Generate grain check code using existing task code with full quantity
  const generateGrainCheckCode = useCallback((grainTask) => {
    // Use full quantity since this task is removed from main cooking
    return generateCode([grainTask]);
  }, []);

  // Clean localStorage at component mount
  useEffect(() => {
    const clearCookingData = () => {
      try {
        const storedTask = localStorage.getItem("kitchenTask");
        if (storedTask) {
          try {
            const parsedTask = JSON.parse(storedTask);
            const timestamp = parsedTask?.timestamp;

            if (
              !timestamp ||
              new Date() - new Date(timestamp) > 2 * 60 * 60 * 1000
            ) {
              console.log("Clearing outdated kitchen task from localStorage");
              localStorage.removeItem("kitchenTask");
            }
          } catch (error) {
            console.error("Invalid kitchen task data, removing:", error);
            localStorage.removeItem("kitchenTask");
          }
        }
      } catch (error) {
        console.error("Error during initial cleanup:", error);
      }
    };

    clearCookingData();
  }, []);

  // Effect for smooth transitions between states
  useEffect(() => {
    setTransition(false);
    const timer = setTimeout(() => {
      setTransition(true);
    }, 100);
    return () => clearTimeout(timer);
  }, [cookingStatus]);

  // Generate security checks based on tasks
  useEffect(() => {
    const generateSecurityChecks = () => {
      const checks = [];

      // Chimney check
      const hasChimneyOn = tasks.some((t) => t.name === "Chimney On");
      const hasChimneyOff = tasks.some((t) => t.name === "Chimney Off");
      if (hasChimneyOn) {
        checks.push({
          id: SECURITY_CHECKS.CHIMNEY_CHECK,
          message: hasChimneyOff
            ? "I confirm chimney is properly OFF before starting"
            : "This recipe doesn't include chimney OFF, are you sure?",
          critical: !hasChimneyOff,
        });
      }

      // Lid check
      const hasOpenLid = tasks.some((t) => t.name === "Open Lid");
      const hasPourActions = tasks.some((t) => t.category === "Pour Action");

      if (hasOpenLid) {
        checks.push({
          id: SECURITY_CHECKS.LID_CHECK,
          message: "I confirm the lid is CLOSED before starting",
          critical: true,
        });
      }

      if (!hasOpenLid && hasPourActions) {
        checks.push({
          id: SECURITY_CHECKS.LID_OPEN_CHECK,
          message:
            "Pour actions detected without lid opening, confirm manual lid opening",
          critical: true,
        });
      }

      // NutriPot utensil check
      const hasNutriPotAction = tasks.some(
        (t) =>
          t.category === "Nutripot Action" ||
          t.cookIn === "np1" ||
          t.cookIn === "np2"
      );
      if (hasNutriPotAction) {
        checks.push({
          id: SECURITY_CHECKS.NUTRIPOT_CHECK,
          message:
            "I confirm NutriPot utensil is properly placed and securely attached",
          critical: true,
        });
      }

      checks.push({
        id: SECURITY_CHECKS.KITCHEN_READY,
        message: "I confirm all kitchen lights are GREEN and ready",
        critical: true,
      });

      return checks;
    };

    setSecurityChecks(generateSecurityChecks());
  }, [tasks]);

  // Handle socket connection status changes
  useEffect(() => {
    // Wait for settings to be loaded before proceeding
    if (!settingsLoaded) {
      console.log("⏳ Waiting for settings to load...");
      return;
    }

    const requiredConnections =
      isConnected && (enableUtensilCheck ? isUtensilSocketConnected : true);

    if (requiredConnections && flowStage === "waiting-connections") {
      console.log("Required sockets connected, moving to signal check", {
        isConnected,
        enableUtensilCheck,
        isUtensilSocketConnected,
        enableSignalCheck,
      });

      // If signal check is disabled, skip directly to next stage
      if (!enableSignalCheck) {
        console.log("🔧 Signal check disabled, skipping to next stage...");
        if (!enableUtensilCheck || (!hasNP1 && !hasNP2)) {
          // Skip utensil check, check if dispenser check is needed
          if (enableDispenserCheck && hasDispenserTasks()) {
            setFlowStage("checking-dispensers");
          } else {
            setFlowStage("security-checks");
          }
        } else {
          setFlowStage("checking-utensils");
        }
      } else {
        setFlowStage("checking-signals");
      }
      setCookingStatus("checking-kitchen");
    }
  }, [
    isConnected,
    isUtensilSocketConnected,
    flowStage,
    enableUtensilCheck,
    enableSignalCheck,
    settingsLoaded,
    hasNP1,
    hasNP2,
  ]);

  // Function to handle dispenser check retry (smart retry)
  const handleDispenserRetry = useCallback(() => {
    console.log("🔄 Retrying failed dispensers only...");
    setCookingStatus("checking-kitchen");
    setTimeout(() => {
      checkDispensers(true); // Pass true for retry mode
    }, 1000);
  }, [checkDispensers]);

  // Handle flow stage changes
  const handleFlowStageChange = useCallback(
    (newStage, isRetry = false) => {
      console.log("Flow stage changing to:", newStage, "isRetry:", isRetry);
      setFlowStage(newStage);

      if (newStage === "checking-dispensers") {
        setCookingStatus("checking-kitchen");
        // Start dispenser checking automatically
        if (enableDispenserCheck && hasDispenserTasks()) {
          setTimeout(() => {
            checkDispensers(isRetry); // Pass isRetry parameter
          }, 1000);
        } else {
          // Skip to security checks if no dispenser tasks or disabled
          setTimeout(() => {
            setFlowStage("security-checks");
          }, 500);
        }
      } else if (newStage === "security-checks") {
        setCookingStatus("connecting");
      }
    },
    [enableDispenserCheck, hasDispenserTasks, checkDispensers]
  );

  // Start cooking process
  const startCooking = useCallback(async () => {
    try {
      setCookingStatus("cooking");

      // Determine which code and tasks to use
      let finalCode = code;
      let finalTasks = tasks;

      // If dispenser check is enabled and completed, use updated tasks
      if (
        enableDispenserCheck &&
        dispenserCheckStatus.completed &&
        dispenserCheckStatus.updatedTasks
      ) {
        console.log("🔧 Using updated tasks after dispenser check");
        finalTasks = dispenserCheckStatus.updatedTasks;
        finalCode = generateCode(finalTasks);
        console.log("🔧 Generated new code from updated tasks:", finalCode);
      } else if (!enableDispenserCheck) {
        console.log("🔧 Dispenser check disabled, using original code");
      } else {
        console.log("🔧 Using original code and tasks");
      }

      console.log("🔧 Starting cooking process with final code:", finalCode);
      console.log("🔧 Starting cooking process with final tasks:", finalTasks);
      const response = await emitWithTimeout(
        "cook-now",
        finalCode,
        SOCKET_TIMEOUT
      );

      if (response === true) {
        setCookingStatus("success");
        reportCookingStatus({ variables: { status: "STARTED" } });

        if (cookingHistoryId) {
          try {
            await updateCookingHistoryStatus({
              variables: {
                id: cookingHistoryId,
                cooking_status: "SUCCESS",
              },
            });
            console.log("Updated cooking history status to SUCCESS");

            if (localStorage.getItem("kitchenTask")) {
              localStorage.removeItem("kitchenTask");
              console.log("Cleared previous recipe from localStorage");
            }
          } catch (err) {
            console.error("Failed to update cooking history status:", err);
          }
        }
      } else {
        setCookingStatus("failed");
        reportCookingStatus({ variables: { status: "FAILED" } });

        if (cookingHistoryId) {
          try {
            await updateCookingHistoryStatus({
              variables: {
                id: cookingHistoryId,
                cooking_status: "FAILED",
              },
            });
          } catch (err) {
            console.error("Failed to update cooking history status:", err);
          }
        }
      }
    } catch (error) {
      setCookingStatus(error.message === "timeout" ? "timeout" : "failed");

      if (cookingHistoryId) {
        try {
          await updateCookingHistoryStatus({
            variables: {
              id: cookingHistoryId,
              cooking_status: "FAILED",
            },
          });
        } catch (err) {
          console.error("Failed to update cooking history status:", err);
        }
      }
    }
  }, [
    cookingHistoryId,
    reportCookingStatus,
    updateCookingHistoryStatus,
    code,
    tasks,
    enableDispenserCheck,
    dispenserCheckStatus,
    generateCode,
  ]);

  // Handle flow completion (when all checks are done)
  const handleFlowComplete = useCallback(
    (success) => {
      console.log("Complete flow finished:", success);
      if (success) {
        console.log(
          "✅ All kitchen checks completed successfully, starting cooking process..."
        );
        setFlowStage("cooking"); // Hide the KitchenFlow component
        startCooking();
      } else {
        setCookingStatus("connecting");
      }
    },
    [startCooking]
  );

  // Handle reset functionality
  const handleReset = useCallback(async () => {
    setResetStatus((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const response = await new Promise((resolve, reject) => {
        if (!socket?.connected) {
          return reject(new Error("Socket not connected"));
        }

        const timer = setTimeout(() => {
          reject(new Error("timeout"));
        }, SOCKET_TIMEOUT);

        socket.emit("reset", (response) => {
          clearTimeout(timer);
          resolve(response);
        });
      });

      if (response) {
        setResetStatus({ loading: false, error: null, tried: true });
      }
    } catch (error) {
      setResetStatus({ loading: false, error: error.message, tried: true });
    }
  }, [socket]);

  // Handle abort cooking
  const handleAbort = useCallback(async () => {
    try {
      await new Promise((resolve, reject) => {
        if (!socket?.connected) {
          return reject(new Error("Socket not connected"));
        }

        const timer = setTimeout(() => {
          reject(new Error("timeout"));
        }, SOCKET_TIMEOUT);

        socket.emit("abort", (response) => {
          clearTimeout(timer);
          resolve(response);
        });
      });

      setCookingStatus("failed");

      if (cookingHistoryId) {
        try {
          await updateCookingHistoryStatus({
            variables: {
              id: cookingHistoryId,
              cooking_status: "FAILED",
            },
          });
        } catch (err) {
          console.error(
            "Failed to update cooking history status after abort:",
            err
          );
        }
      }
    } catch (error) {
      console.error("Abort failed:", error);
    }
  }, [socket, cookingHistoryId, updateCookingHistoryStatus]);

  // Timer management
  useEffect(() => {
    if (cookingStatus === "cooking") {
      timerRef.current = setInterval(() => {
        setElapsedTime((prev) => prev + 1);
      }, 1000);
    }
    return () => clearInterval(timerRef.current);
  }, [cookingStatus]);

  // Clean up localStorage when cooking succeeds
  useEffect(() => {
    if (cookingStatus === "success") {
      if (localStorage.getItem("kitchenTask")) {
        localStorage.removeItem("kitchenTask");
        console.log(
          "Cleared previous recipe from localStorage on cooking success"
        );
      }
    }
  }, [cookingStatus]);

  // Helper function to get recipe display name
  const getRecipeDisplayName = useCallback(() => {
    if (isMergedRecipes && recipe?.merged_recipe_names?.length) {
      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            mb: 1,
          }}
        >
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            Multiple Recipes
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1,
              justifyContent: "center",
            }}
          >
            {recipe.merged_recipe_names.map((name, index) => (
              <Chip
                key={index}
                label={name}
                color={index === 0 ? "primary" : "secondary"}
                sx={{ fontWeight: 500 }}
              />
            ))}
          </Box>
        </Box>
      );
    }
    return (
      <Typography variant="h5" fontWeight="bold" gutterBottom>
        {recipe?.recipe_name || `Recipe ${recipe?.id || recipe_id}`}
      </Typography>
    );
  }, [recipe, isMergedRecipes, recipe_id]);

  // Sequential lid operations for safety
  const performSequentialLidOperations = useCallback(async () => {
    const requiredUtensils = [];
    if (hasNP1) requiredUtensils.push("np1");
    if (hasNP2) requiredUtensils.push("np2");

    console.log("🔧 Starting sequential lid operations for:", requiredUtensils);

    for (const utensil of requiredUtensils) {
      try {
        console.log(`🔓 Opening lid for ${utensil}...`);

        // Open lid
        const openCode = `async function start() {
  this.utensil = '${utensil}';
  await lidOpen('${utensil}');
}
start();`;

        const openResponse = await emitWithTimeout(
          "cook-now",
          openCode,
          SOCKET_TIMEOUT
        );
        console.log(`📡 Lid open response for ${utensil}:`, openResponse);

        if (openResponse !== true) {
          console.warn(`⚠️ Lid open may have failed for ${utensil}`);
        }

        // Wait between operations for safety
        await new Promise((resolve) => setTimeout(resolve, 1000));

        // Check utensil (with timeout)
        let utensilDetected = false;
        try {
          console.log(`🔍 Checking utensil: ${utensil}`);
          const timeoutPromise = new Promise((_, reject) => {
            setTimeout(() => reject(new Error("Utensil check timeout")), 60000);
          });

          utensilDetected = await Promise.race([
            checkUtensil(utensil),
            timeoutPromise,
          ]);

          console.log(`📍 Utensil ${utensil} detected:`, utensilDetected);
        } catch (error) {
          console.error(`❌ Utensil check failed for ${utensil}:`, error);
          utensilDetected = false;
        }

        // Wait before closing
        await new Promise((resolve) => setTimeout(resolve, 1000));

        // Close lid for safety
        console.log(`🔒 Closing lid for ${utensil}...`);
        const closeCode = `async function start() {
  this.utensil = '${utensil}';
  await lidClose('${utensil}');
}
start();`;

        const closeResponse = await emitWithTimeout(
          "cook-now",
          closeCode,
          SOCKET_TIMEOUT
        );
        console.log(`📡 Lid close response for ${utensil}:`, closeResponse);

        if (closeResponse !== true) {
          console.warn(`⚠️ Lid close may have failed for ${utensil}`);
        }

        // Wait between different utensils for safety
        if (utensil !== requiredUtensils[requiredUtensils.length - 1]) {
          await new Promise((resolve) => setTimeout(resolve, 2000));
        }
      } catch (error) {
        console.error(`❌ Error in lid operations for ${utensil}:`, error);
      }
    }

    console.log("✅ Sequential lid operations completed");
  }, [hasNP1, hasNP2, emitWithTimeout, checkUtensil]);

  // Show error page if there's a parse error
  if (parseError) {
    return (
      <ErrorPage
        error={parseError}
        title="Recipe Data Error"
        description="There was an error processing the recipe data. Please try again."
        showBackButton={true}
        onBack={() => navigate(-1)}
      />
    );
  }
  // Show error if no tasks are available
  if (!tasks || tasks.length === 0) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert
          severity="warning"
          sx={{
            borderRadius: 2,
            p: 3,
            textAlign: "center",
          }}
        >
          <Typography variant="h6" gutterBottom>
            No Cooking Tasks Found
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            This recipe doesn't have any cooking steps configured. Please check
            the recipe configuration.
          </Typography>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Recipe: {recipe?.recipe_name || "Unknown Recipe"}
            </Typography>
          </Box>
        </Alert>
      </Container>
    );
  }
  // Connection screen - show when settings are loading or connections are needed
  if (
    (!settingsLoaded ||
      !isConnected ||
      (settingsLoaded && enableUtensilCheck && !isUtensilSocketConnected)) &&
    flowStage === "waiting-connections"
  ) {
    return (
      <Box sx={{ height: "100vh", display: "flex", flexDirection: "column" }}>
        <Box
          sx={{
            flexGrow: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            background: `linear-gradient(135deg, ${theme.palette.primary.light}15 0%, ${theme.palette.primary.dark}05 100%)`,
            p: 3,
          }}
        >
          <Paper
            elevation={6}
            sx={{
              maxWidth: 600,
              width: "100%",
              p: 4,
              borderRadius: 4,
              textAlign: "center",
              boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
              background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.grey[50]} 100%)`,
            }}
          >
            <Typography
              variant="h4"
              gutterBottom
              color="primary"
              fontWeight={700}
            >
              Connecting to Smart Kitchen
            </Typography>

            <Box sx={{ width: "100%", my: 4 }}>
              <LinearProgress
                color="primary"
                sx={{
                  height: 12,
                  borderRadius: 6,
                  "& .MuiLinearProgress-bar": {
                    borderRadius: 6,
                    background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                  },
                }}
              />
            </Box>

            <Typography variant="body1" color="text.secondary" paragraph>
              Please wait while we establish secure connections to your kitchen
              devices.
            </Typography>

            {/* Connection Status Details */}
            <Box sx={{ mt: 3, mb: 3 }}>
              <Stack spacing={2}>
                {/* Settings Loading Status */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    p: 2,
                    bgcolor: theme.palette.grey[50],
                    borderRadius: 2,
                  }}
                >
                  <Typography variant="body2">Kitchen Settings</Typography>
                  <Chip
                    label={settingsLoaded ? "Loaded" : "Loading..."}
                    color={settingsLoaded ? "success" : "warning"}
                    size="small"
                  />
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    p: 2,
                    bgcolor: theme.palette.grey[50],
                    borderRadius: 2,
                  }}
                >
                  <Typography variant="body2">
                    Kitchen Control System
                  </Typography>
                  <Chip
                    label={isConnected ? "Connected" : "Connecting..."}
                    color={isConnected ? "success" : "warning"}
                    size="small"
                  />
                </Box>
                {settingsLoaded && enableUtensilCheck && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      p: 2,
                      bgcolor: theme.palette.grey[50],
                      borderRadius: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Utensil Detection System
                    </Typography>
                    <Chip
                      label={
                        isUtensilSocketConnected ? "Connected" : "Connecting..."
                      }
                      color={isUtensilSocketConnected ? "success" : "warning"}
                      size="small"
                    />
                  </Box>
                )}
                {settingsLoaded && !enableUtensilCheck && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      p: 2,
                      bgcolor: theme.palette.grey[100],
                      borderRadius: 2,
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Utensil Detection System
                    </Typography>
                    <Chip label="Disabled" color="default" size="small" />
                  </Box>
                )}
              </Stack>
            </Box>

            {recipe && (
              <Box
                sx={{
                  mt: 3,
                  p: 2,
                  bgcolor: theme.palette.grey[50],
                  borderRadius: 2,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Preparing to cook: <strong>{recipe.recipe_name}</strong>
                </Typography>
                {tasks.length > 0 && (
                  <Typography variant="caption" color="text.secondary">
                    {tasks.length} cooking steps ready
                  </Typography>
                )}
              </Box>
            )}

            {(connectionError || utensilConnectionError) && (
              <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
                Connection Error: {connectionError || utensilConnectionError}
              </Alert>
            )}

            <Box sx={{ mt: 3 }}>
              <Typography variant="body2" color="text.secondary">
                Make sure your devices are powered on and connected to the same
                network.
              </Typography>
            </Box>
          </Paper>
        </Box>
      </Box>
    );
  }

  // Kitchen Checking View with enhanced signal display
  if (cookingStatus === "checking-kitchen") {
    return (
      <Box sx={{ height: "100vh", display: "flex", flexDirection: "column" }}>
        <ConnectionStatus
          isConnected={
            isConnected &&
            (enableUtensilCheck ? isUtensilSocketConnected : true)
          }
        />
        <Box
          sx={{
            flexGrow: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            background: `linear-gradient(135deg, ${theme.palette.primary.light}15 0%, ${theme.palette.primary.dark}05 100%)`,
            p: 3,
          }}
        >
          <Box
            sx={{ maxWidth: 800, mx: "auto", p: 2, mb: 3, textAlign: "center" }}
          >
            {getRecipeDisplayName()}
          </Box>

          {/* Original KitchenFlow for utensil checks and security */}
          <KitchenFlow
            checkSignals={checkSignals}
            checkUtensil={checkUtensil}
            emitWithTimeout={emitWithTimeout}
            tasks={tasks}
            isMergedRecipes={isMergedRecipes}
            onFlowComplete={handleFlowComplete}
            flowStage={flowStage}
            onStageChange={handleFlowStageChange}
            performSequentialLidOperations={performSequentialLidOperations}
            enableSignalCheck={enableSignalCheck}
            enableUtensilCheck={enableUtensilCheck}
            enableDispenserCheck={enableDispenserCheck}
            dispenserCheckStatus={dispenserCheckStatus}
            hasDispenserTasks={hasDispenserTasks}
            getFirstGrainTask={getFirstGrainTask}
            getFirstSpiceTask={getFirstSpiceTask}
            onDispenserRetry={handleDispenserRetry}
          />
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        position: "relative",
        minHeight: "100vh",
        backgroundColor: theme.palette.background.default,
        backgroundImage: `radial-gradient(${theme.palette.primary.light}10 1px, transparent 1px)`,
        backgroundSize: "20px 20px",
      }}
    >
      <ConnectionStatus
        isConnected={
          isConnected && (enableUtensilCheck ? isUtensilSocketConnected : true)
        }
      />

      {/* Show KitchenFlow for security checks if we're in the new flow */}
      {flowStage === "security-checks" && cookingStatus !== "cooking" && (
        <Fade in={transition} timeout={800}>
          <Box>
            <Box
              sx={{
                maxWidth: 800,
                mx: "auto",
                p: 2,
                mb: 3,
                textAlign: "center",
              }}
            >
              {getRecipeDisplayName()}
            </Box>

            <KitchenFlow
              checkSignals={checkSignals}
              checkUtensil={checkUtensil}
              emitWithTimeout={emitWithTimeout}
              tasks={tasks}
              isMergedRecipes={isMergedRecipes}
              onFlowComplete={handleFlowComplete}
              flowStage={flowStage}
              onStageChange={handleFlowStageChange}
              enableSignalCheck={enableSignalCheck}
              enableUtensilCheck={enableUtensilCheck}
              enableDispenserCheck={enableDispenserCheck}
              dispenserCheckStatus={dispenserCheckStatus}
              hasDispenserTasks={hasDispenserTasks}
              getFirstGrainTask={getFirstGrainTask}
              getFirstSpiceTask={getFirstSpiceTask}
              onDispenserRetry={handleDispenserRetry}
            />
          </Box>
        </Fade>
      )}

      {/* Legacy SecurityChecks component for backward compatibility */}
      <Fade
        in={
          transition &&
          cookingStatus === "connecting" &&
          flowStage !== "security-checks"
        }
        timeout={800}
      >
        <Box
          sx={{
            display:
              cookingStatus === "connecting" && flowStage !== "security-checks"
                ? "block"
                : "none",
          }}
        >
          <Box
            sx={{ maxWidth: 800, mx: "auto", p: 2, mb: 3, textAlign: "center" }}
          >
            {getRecipeDisplayName()}
          </Box>

          <SecurityChecks
            checks={securityChecks}
            completedChecks={completedChecks}
            onCheckToggle={setCompletedChecks}
            onStart={startCooking}
            onReset={resetSocket}
          />
        </Box>
      </Fade>

      <Fade in={transition && cookingStatus === "cooking"} timeout={100000}>
        <Box sx={{ display: cookingStatus === "cooking" ? "block" : "none" }}>
          <CookingInProgress
            recipe={recipe}
            tasks={tasks}
            elapsedTime={elapsedTime}
            onAbort={handleAbort}
            onReset={handleReset}
            resetStatus={resetStatus}
            isMergedRecipes={isMergedRecipes}
            onNavigateBack={() => navigate(`/cook-recipe/${recipe_id}`)}
          />
        </Box>
      </Fade>

      <Fade in={transition && cookingStatus === "success"} timeout={800}>
        <Box sx={{ display: cookingStatus === "success" ? "block" : "none" }}>
          <Box
            sx={{ maxWidth: 800, mx: "auto", p: 2, mb: 3, textAlign: "center" }}
          >
            {getRecipeDisplayName()}
          </Box>
          <CookingSuccess
            recipe={recipe}
            elapsedTime={elapsedTime}
            onReset={handleReset}
            resetStatus={resetStatus}
            onNavigateBack={() => navigate(-1)}
            isMergedRecipes={isMergedRecipes}
          />
        </Box>
      </Fade>

      <Fade
        in={
          transition &&
          (cookingStatus === "failed" || cookingStatus === "timeout")
        }
        timeout={800}
      >
        <Box
          sx={{
            display:
              cookingStatus === "failed" || cookingStatus === "timeout"
                ? "block"
                : "none",
          }}
        >
          <Box
            sx={{ maxWidth: 800, mx: "auto", p: 2, mb: 3, textAlign: "center" }}
          >
            {getRecipeDisplayName()}
          </Box>
          <CookingFailure
            status={cookingStatus}
            recipe={recipe}
            resetStatus={resetStatus}
            onReset={handleReset}
            onNavigateBack={() => navigate(`/cook-recipe/${recipe_id}`)}
            isMergedRecipes={isMergedRecipes}
          />
        </Box>
      </Fade>
    </Box>
  );
};

export default CookingProcess;
